import type { UndoStore } from "../store/undoStore";
import { getUndoStore } from "../store/undoStore";
import tombstoneOperations from "owa-mail-list-tombstone";
import type { TableView } from "owa-mail-list-store";
import type { ActionSource } from "owa-mail-store";
import type ApplyMessageActionResponseMessage from "owa-service/lib/contract/ApplyMessageActionResponseMessage";
import type MoveItemResponse from "owa-service/lib/contract/MoveItemResponse";
import { mutatorAction } from "satcheljs";
import { PerformanceDatapoint } from "owa-analytics";
import { isNotificationChannelInitialized } from "owa-mail-notification-channel-ready/lib/isNotificationChannelInitialized";
import undoWhenNotificationChannelNotReady from "owa-mail-actions/lib/table-loading/undoWhenNotificationChannelNotReady";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";

export interface UndoStoreState {
    store: UndoStore;
}

declare type UndoResponseType = ApplyMessageActionResponseMessage | MoveItemResponse | void;

/**
 * A single per-mailbox undo reverser. Cross-mailbox triage actions register
 * one action per mailbox group; single-mailbox actions register a one-action composite.
 */
export interface UndoAction {
    undo: () => Promise<UndoResponseType>;
    actionTableView: TableView | null;
}

/**
 * Only supporting a "stack" of one (last undo action). For cross-mailbox actions this
 * holds multiple per-mailbox actions that are dispatched in parallel on undo.
 * Used when the caller of the fanout can ad-hoc create a composite undo action.
 */
let lastUndoAction: UndoAction[] | null = null;

// Captured callback for undo actions
// This is used when the fanout undo cannot be created ad-hoc by the original caller and needs to be captured while performing the fanout
type UndoCaptureCallback = (action: UndoAction) => void;
let activeUndoCapture: UndoCaptureCallback | null = null;

const setUndoableActionMutator = mutatorAction("setUndoableAction", (hasUndoableActionToSet: boolean) => {
    const store = getUndoStore();
    store.hasUndoableAction = hasUndoableActionToSet;
});

export function addActionToUndoStack(undoAction: (() => Promise<UndoResponseType>) | null, actionTableView: TableView | null): void {
    if (undoAction !== null) {
        addCompositeActionToUndoStack([{ undo: undoAction, actionTableView }]);
    }
}

/**
 * Registers a multi-action undo composite. Each action is reversed in parallel when the user undoes.
 * Used by cross-mailbox (UIB) triage actions to register one reverser per mailbox.
 */
export function addCompositeActionToUndoStack(actions: UndoAction[]): void {
    if (actions.length === 0) {
        return;
    }
    if (activeUndoCapture) {
        for (const action of actions) {
            activeUndoCapture(action);
        }
        return;
    }
    lastUndoAction = actions;
    setUndoableActionMutator(true);
}

/**
 * Runs a function `fn` with undo registrations diverted into `onAction` instead of the global slot.
 * Lets a fan-out caller collect per-mailbox reversers from the single-mailbox fanout
 * helpers and combine them into one composite. Nested scopes restore the outer scope on exit.
 */
export async function runWithUndoCapture<T>(onAction: UndoCaptureCallback, fn: () => Promise<T>): Promise<T> {
    const previous = activeUndoCapture;
    activeUndoCapture = onAction;
    try {
        return await fn();
    }
    finally {
        activeUndoCapture = previous;
    }
}

/**
 * Test-only accessor for the currently registered undo composite. Returns the
 * full array so tests can inspect every member; production code should call
 * `undo()` to run all reversers rather than peeking at the slot.
 * @return The composite of registered undo actions, or null if none is registered.
 */
export function getLastUndoActionForTest(): UndoAction[] | null {
    return lastUndoAction;
}

/**
 * Clears last undoable action (to null)
 */
export function clearLastUndoableAction(): void {
    lastUndoAction = null;
    setUndoableActionMutator(false);
}

/**
 * Returns if last undoable action is empty
 * @return If last undoable action is empty
 */
export function hasUndoableAction(): boolean {
    return getUndoStore().hasUndoableAction;
}

/**
 * Undoes the last undoable action (if it exists).
 */
export function undo(actionSource: ActionSource): Promise<void> {
    const mailboxInfo = getModuleContextMailboxInfo();
    
    const dp = new PerformanceDatapoint("TnS_Undo", mailboxInfo, { isScorecardEvent: true });
    dp.addCustomData([actionSource]);
    
    const actions = lastUndoAction;
    
    // Need to remove tombstone entries for the table(s) where the undo action is being taken. If the
    // action was performed on a row that is in tombstone then we will not honor the row notification
    // when the action gets undone so the row won't come back. Tombstones are scoped per-tableView, so
    // we clear the exact source tableView the action was taken from (Note: in UIB tables, rows all
    // share the same source unified-inbox table).
    const tableViewsToClear = new Map<string, TableView>();
    if (actions) {
        for (const action of actions) {
            if (action.actionTableView) {
                tableViewsToClear.set(action.actionTableView.id, action.actionTableView);
            }
        }
    }
    if (tableViewsToClear.size > 0) {
        for (const tableView of tableViewsToClear.values()) {
            tombstoneOperations.clearMapForTableViewMutator(tableView);
        }
    }
    else {
        // There are cases when we do not have a tableView e.g deleteItems which can be performed from
        // various places like RP, fileshub etc. Once (VSO: 45716 - [Refactor] Remove usage of
        // getSelectedTableView from delete item) is fixed we shall pass the correct tableView to clear.
        tombstoneOperations.removeAllMutator();
    }
    
    // actions can be null if lazy loading of undo doesn't finish before user performs another action
    // that clears the undo stack.
    let undoChain: Promise<void> = Promise.resolve();
    if (actions !== null) {
        // Perform undo for every action in parallel.
        undoChain = Promise.allSettled(actions.map(action => action.undo())).then(results => {
            if (!isNotificationChannelInitialized()) {
                undoWhenNotificationChannelNotReady();
            }
            
            const failedResult = results.find(result => result.status === "rejected");
            if (failedResult?.status === "rejected") {
                return dp.endWithUnhandledError(failedResult.reason);
            }
            
            return dp.end();
        });
        
        // Must clear stack so user can't try to undo same action again. Also clears UI.
        clearLastUndoableAction();
    }
    else {
        dp.end();
    }
    
    return undoChain;
}

/**
 * This is just exported for test purposes and should not be invoked in product code
 */
export function test_reset(): void {
    lastUndoAction = null;
    activeUndoCapture = null;
}
